import React from 'react';
import { TableBody as MuiTableBody, TableRow, TableCell, IconButton, Box, Tooltip, Typography } from '@mui/material';
import { Edit, Delete as DeleteIcon, DirectionsRun, Accessibility } from '@mui/icons-material';
import { TableColumn, BaseTableRow, EditableComponentProps } from '../types';


interface TableBodyProps<T extends BaseTableRow = BaseTableRow> {
  headers: TableColumn<T>[];
  data: T[];
  editingRowId: string | number | null;
  isAddingNewRow?: boolean;
  newRowTemplate?: T;
  EditableComponent?: React.ComponentType<EditableComponentProps<T>>;
  onEdit: (row: T, index: number) => void;
  onDelete: (row: T, index: number) => void;
  onActive: (row: T, index: number) => void;
  onInactive: (row: T, index: number) => void;
  onSave: (updatedRow: T) => void;
  onSaveNewRow: (newRow: T) => void;
  onCancel: (row: T, index: number) => void;
}

export function TableBody<T extends BaseTableRow>({
  headers,
  data,
  editingRowId,
  isAddingNewRow,
  newRowTemplate,
  EditableComponent,
  onEdit,
  onDelete,
  onActive,
  onInactive,
  onSave,
  onSaveNewRow,
  onCancel,
}: TableBodyProps<T>): React.ReactElement {
  const isRowEditing = (row: T, index: number) => {
    return editingRowId === (row.id || index) || row.isEditing;
  };
  console.log({ isAddingNewRow, newRowTemplate, EditableComponent });
  return (
    <MuiTableBody>
      {isAddingNewRow && newRowTemplate && EditableComponent && (
        <TableRow sx={{ '& > td': { lineHeight: 1.2, paddingX: '5px' } }} className='TableCellBorder'>
          <EditableComponent
            row={newRowTemplate}
            data={data}
            isNewRow={true}
            onSave={onSaveNewRow}
            index={data.length}
            onCancel={() => {
              const tempId = editingRowId;
              if (tempId && typeof tempId === 'string' && tempId.startsWith('new-')) {
                onCancel(newRowTemplate, -1);
              }
            }}
          />
        </TableRow>
      )}
      {data.map((row, index) => {
        const isEditing = isRowEditing(row, index);

        return (
          <TableRow  className='TableCellBorder' key={row.id || index} sx={{ '& > td': { lineHeight: 1.2, paddingX: '5px' } }}>
            {isEditing && EditableComponent ? (
              <EditableComponent
                row={row}
                data={data}
                isNewRow={false}
                index={index}
                onCancel={() => onCancel(row, index)}
                onSave={onSave} 
              />
            ) : (
              <>
                {headers.map((header) => {
                  const key = header.key as keyof T;
                  const cellValue = key in row ? (row[key] as any) : undefined;
                  return (
                    <TableCell key={String(header.key)} align={header.align || 'left'} sx={{ padding: 1, paddingX: '5px'}}>
                      {header.render ? header.render(cellValue, row, index) : cellValue ?? '-'}
                    </TableCell>
                  );
                })}
                <TableCell
                  sx={{
                    padding: 0,
                    paddingX: '5px',
                    position: 'sticky',
                    right: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 9,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                    {row.isEditable && (
                      <Tooltip title="Edit" placement="top" disableInteractive arrow>
                        <IconButton
                          size="small"
                          onClick={() => onEdit(row, index)}
                          sx={{
                            padding: '5px',
                            backgroundColor: '#b1d1f97d',
                            '&:hover': {
                              backgroundColor: '#b1d1f9',
                            },
                          }}
                          disableRipple
                        >
                          <Edit sx={{ fontSize: '15px', color: '#6096d9' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {row.isDeletable && (
                      <Tooltip title="Delete" placement="top" disableInteractive arrow>
                        <IconButton
                          size="small"
                          onClick={() => onDelete(row, index)}
                          sx={{
                            padding: '5px',
                            backgroundColor: '#fbc7c78c',
                            '&:hover': {
                              backgroundColor: '#fbc7c7',
                            },
                          }}
                          disableRipple
                        >
                          <DeleteIcon sx={{ fontSize: '15px', color: '#eb6a6a' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {row.isActive !== undefined && (
                      <Tooltip title={row.isActive ? 'Mark as In-Active' : 'Mark as Active'} placement="top" disableInteractive arrow>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (row.isActive) {
                              onInactive(row, index);
                            } else {
                              onActive(row, index);
                            }
                          }}
                          sx={{
                            padding: '4px',
                            width: '28px',
                            height: '28px',
                            backgroundColor: !row.isActive ? '#F6B26B' : '#FEF3C7',
                            '&:hover': {
                              backgroundColor: !row.isActive ? '#e0a15f' : '#e6dbb3',
                            },
                          }}
                          disableRipple
                        >
                          {row.isActive ? (
                            <DirectionsRun
                              sx={{
                                fontSize: '15px',
                                color: '#E29819',
                              }}
                            />
                          ) : (
                            <Accessibility
                              sx={{
                                fontSize: '15px',
                                color: '#ffff',
                              }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </>
            )}
          </TableRow>
        );
      })}
    </MuiTableBody>
  );
}
